import { useState, useRef } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  Upload, 
  FileSpreadsheet, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Download,
  Loader2,
  ArrowRight
} from 'lucide-react';

export default function ImportPage() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
      ];
      
      if (!validTypes.includes(selectedFile.type) && 
          !selectedFile.name.endsWith('.csv') && 
          !selectedFile.name.endsWith('.xlsx') && 
          !selectedFile.name.endsWith('.xls')) {
        toast.error('Format de fichier non supporté. Utilisez CSV ou Excel.');
        return;
      }
      
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await api.post('/import/employees', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      clearInterval(progressInterval);
      setProgress(100);
      setResult(response.data);
      toast.success('Import terminé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const templateColumns = [
    // Champs obligatoires
    { name: 'Prénom', required: true },
    { name: 'Nom', required: true },
    { name: 'Poste', required: true },
    // Champs optionnels - Identité
    { name: 'Email', required: false },
    { name: 'Téléphone', required: false },
    { name: 'Matricule', required: false },
    { name: 'Sexe', required: false, hint: 'Homme/Femme' },
    { name: 'Date de naissance', required: false, hint: 'JJ/MM/AAAA' },
    { name: 'Situation familiale', required: false, hint: 'Célibataire/Marié(e)/etc.' },
    // Champs optionnels - Professionnel
    { name: 'Département', required: false },
    { name: 'Site', required: false },
    { name: 'Manager (email)', required: false },
    // Champs optionnels - RH
    { name: 'Type de contrat', required: false, hint: 'CDI/CDD/Stage/etc.' },
    { name: 'Catégorie professionnelle', required: false, hint: 'Cadre/Agent de maîtrise/etc.' },
    { name: 'Date embauche', required: false, hint: 'JJ/MM/AAAA' },
    { name: 'Type entrée', required: false, hint: 'Nouvelle recrue/Transfert entrant/etc.' },
    { name: 'Statut', required: false, hint: 'Actif/Inactif' },
  ];

  return (
    <DashboardLayout title="Import">
      <div className="max-w-4xl mx-auto space-y-6" data-testid="import-page">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Importer des employés
          </h1>
          <p className="text-slate-600">
            Importez ou mettez à jour vos employés via un fichier CSV ou Excel
          </p>
        </div>

        {/* Template Download */}
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-webtech-blue" />
              Format du fichier
            </CardTitle>
            <CardDescription>
              Votre fichier doit contenir les colonnes suivantes (ordre flexible)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Champs obligatoires */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Champs obligatoires</p>
                <div className="flex flex-wrap gap-2">
                  {templateColumns.filter(c => c.required).map((col) => (
                    <Badge key={col.name} className="bg-red-100 text-red-700 border-red-200">
                      {col.name} *
                    </Badge>
                  ))}
                </div>
              </div>
              
              {/* Champs optionnels */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Champs optionnels</p>
                <div className="flex flex-wrap gap-2">
                  {templateColumns.filter(c => !c.required).map((col) => (
                    <Badge 
                      key={col.name} 
                      variant="outline" 
                      className="bg-slate-50"
                      title={col.hint || ''}
                    >
                      {col.name}
                      {col.hint && <span className="text-slate-400 ml-1">({col.hint})</span>}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600 mt-4">
              <p className="font-medium mb-2">Notes :</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Email optionnel</strong> : Si fourni, il sert d'identifiant pour les mises à jour</li>
                <li>Les employés existants (même email ou matricule) seront mis à jour</li>
                <li>Les nouveaux employés seront créés automatiquement</li>
                <li><strong>Manager</strong> : Peut être renseigné par email, matricule ou nom complet (ex: "Jean Dupont")</li>
                <li>Les managers sont assignés après création de tous les employés (import en 2 passes)</li>
                <li>Les dates doivent être au format JJ/MM/AAAA ou AAAA-MM-JJ</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Upload Zone */}
        {!result && (
          <Card>
            <CardContent className="p-6">
              <div 
                className={`
                  border-2 border-dashed rounded-xl p-8 text-center
                  transition-colors cursor-pointer
                  ${file ? 'border-webtech-blue bg-webtech-blue/5' : 'border-slate-300 hover:border-slate-400'}
                `}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="file-input"
                />
                
                {file ? (
                  <div className="space-y-3">
                    <div className="w-16 h-16 bg-webtech-blue/10 rounded-full flex items-center justify-center mx-auto">
                      <FileSpreadsheet className="w-8 h-8 text-webtech-blue" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{file.name}</p>
                      <p className="text-sm text-slate-500">
                        {(file.size / 1024).toFixed(1)} Ko
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReset();
                      }}
                    >
                      Changer de fichier
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto">
                      <Upload className="w-8 h-8 text-slate-400" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        Cliquez pour sélectionner un fichier
                      </p>
                      <p className="text-sm text-slate-500">
                        ou glissez-déposez ici (CSV, Excel)
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {file && !uploading && (
                <div className="mt-6 flex justify-center">
                  <Button 
                    onClick={handleUpload}
                    className="bg-webtech-blue hover:bg-webtech-blue-hover text-white"
                    data-testid="upload-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Lancer l'import
                  </Button>
                </div>
              )}

              {uploading && (
                <div className="mt-6 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Import en cours...</span>
                    <span className="font-medium">{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {result && (
          <Card className="animate-fade-in" data-testid="import-result">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                Import terminé
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{result.created}</p>
                  <p className="text-sm text-green-700">Créés</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{result.updated}</p>
                  <p className="text-sm text-blue-700">Mis à jour</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-purple-600">{result.managersAssigned || 0}</p>
                  <p className="text-sm text-purple-700">Managers assignés</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-red-600">{result.errors}</p>
                  <p className="text-sm text-red-700">Erreurs</p>
                </div>
              </div>

              {result.errorLog && result.errorLog.length > 0 && (
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <p className="font-medium text-red-800">Erreurs détectées :</p>
                  </div>
                  <ul className="text-sm text-red-700 space-y-1">
                    {result.errorLog.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={handleReset}>
                  Nouvel import
                </Button>
                <Button 
                  className="bg-webtech-blue hover:bg-webtech-blue-hover text-white"
                  onClick={() => window.location.href = '/employees'}
                >
                  Voir les employés
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
